import React, { useEffect, useState } from "react";
import {
  FormContainer,
  FormHeader,
  FormField,
  SubmitButton,
  Message,
  ValidationError,
} from "./EditGestanteForm.styles";
import CpfInputField from "../FormField/CpfInputField";
import api from '../../services/api';
import { formatValueErrorMessage } from "../../utils/errorMessage";
import { getUserProfile } from "../../utils/auth";
import ToastManager from '../../components/Toast/ToastManager';
import { useToast } from '../../hooks/useToast';
import { useGestanteFormConfig } from '../../hooks/useGestanteFormConfig';
import IdentidadeGeneroSection, {
  IDENTIDADE_INITIAL_STATE,
  identidadeStateFromGestante,
  montarPayloadIdentidade,
  podeApresentarIdentidade,
  validarIdentidade,
} from "./IdentidadeGeneroSection";
export const MSG_DATA_NASCIMENTO_OBRIGATORIA =
  "Informe a data de nascimento da gestante";

const hojeISO = () => new Date().toISOString().slice(0, 10);

const somenteData = (valor) => (valor ? String(valor).slice(0, 10) : "");

const EditGestanteForm = ({ gestante }) => {
  const [formValues, setFormValues] = useState({
    cpf: "",
    nome: "",
    cns: "",
    municipio_cod_ibge: "",
    data_nascimento: "",
  });

  const [identidade, setIdentidade] = useState(IDENTIDADE_INITIAL_STATE);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [municipios, setMunicipios] = useState([]);
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const { gender_orientation_ui_enabled: identidadeFlag } = useGestanteFormConfig();

  // US-102013: só a data digitada na inclusão manual pode ser corrigida, e só
  // por administrador — a das demais gestantes é definida pelo CadÚnico
  const podeEditarDataNascimento =
    Boolean(gestante?.cadastro_manual) && getUserProfile() === "Administrador";

  // Regra de idade (US-102684): < 18 anos ou sem data de nascimento não
  // coleta; a partir de 18 anos a resposta é obrigatória. Quando a data está
  // em edição a regra segue o valor digitado, espelhando o que o backend fará
  const dataNascimentoEfetiva = podeEditarDataNascimento
    ? formValues.data_nascimento
    : gestante?.data_nascimento;
  const identidadeHabilitada =
    identidadeFlag && podeApresentarIdentidade(dataNascimentoEfetiva);

  const handleIdentidadeChange = (name, value) => {
    setIdentidade((prev) => ({ ...prev, [name]: value }));
    // O erro pertence ao valor que acabou de mudar; mantê-lo faria a mensagem
    // sobreviver à própria causa. Trocar o select também limpa o erro do texto
    // livre, que só é exigido por "OUTRO" e cujo campo desmonta junto — ali a
    // mensagem ficaria invisível na tela e ainda assim barraria o envio
    setValidationErrors((prev) => {
      if (!prev[name] && !prev[`${name}_other`]) return prev;
      const next = { ...prev };
      delete next[name];
      delete next[`${name}_other`];
      return next;
    });
  };

  // Atualiza os valores do formulário quando os dados da gestante estiverem disponíveis
  useEffect(() => {
    if (gestante) {
      setFormValues({
        cpf: gestante.cpf ,
        nome: gestante.nome ,
        cns: gestante.cns ,
        municipio_cod_ibge: gestante.municipio_cod_ibge,
        data_nascimento: somenteData(gestante.data_nascimento),
      });
      setIdentidade(identidadeStateFromGestante(gestante));
    }
  }, [gestante]);

  useEffect(() => {
    const fetchMunicipios = async () => {
      try {
        const response = await api.get(`/municipios/list?page=1&size=900`);
        const data = await response.data;
        setMunicipios(data);
      } catch (error) {
        console.error("Erro ao buscar municípios: ", error);
      }
    };

    fetchMunicipios();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // validation max 15 characters in CNS
    if (name === 'cns') {
      const numbersOnly = value.replace(/\D/g, ''); // remove all non-digits
      if (numbersOnly.length <= 15) {
        setFormValues({ ...formValues, [name]: numbersOnly });
      }
      return;
    }
    
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validação de campos obrigatórios
    const errors = {};
    if (!formValues.nome || formValues.nome.trim() === "") {
      errors.nome = "O nome é obrigatório";
    }
    if (!formValues.municipio_cod_ibge) {
      errors.municipio_cod_ibge = "O município é obrigatório";
    }
    if (podeEditarDataNascimento && !formValues.data_nascimento) {
      errors.data_nascimento = MSG_DATA_NASCIMENTO_OBRIGATORIA;
    }
    if (identidadeHabilitada) {
      Object.assign(errors, validarIdentidade(identidade));
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // A validação acima já cobriu o estado atual do formulário, então o que
    // restou de uma tentativa anterior (validação local ou 422 do backend) está
    // resolvido: manter esses erros travaria o envio por um problema que não
    // existe mais. Quem ainda recusar o payload é o backend, que revalida
    setValidationErrors({});

    setIsSubmitting(true);
    setSuccessMessage("");
    setErrorMessage("");

    const sanitizedCpf = formValues.cpf.replace(/[.-]/g, "");
    const sanitizedCns = formValues?.cns?.length === 0 ? null : formValues?.cns;

    const { data_nascimento: dataNascimento, ...camposBase } = formValues;

    const updatedData = {
      ...camposBase,
      cpf: sanitizedCpf,
      cns: sanitizedCns,
      ...(identidadeHabilitada ? montarPayloadIdentidade(identidade) : {}),
      // A data só acompanha o payload quando é editável; enviá-la para uma
      // gestante do CadÚnico seria recusado com 400 pelo backend
      ...(podeEditarDataNascimento ? { data_nascimento: dataNascimento } : {}),
    };

    try {
      const response = await api.put(`/gestante/${gestante.cpf}/update`, JSON.stringify(updatedData));

      if (response.status===200) {
        showSuccess("Dados atualizados com sucesso!");
      } else {
        const errorData = await response.data;
        showError(errorData.detail || "Erro ao atualizar os dados.");
      }
    } catch(error) {
      // 422 traz uma lista de erros por campo (loc: ['body', 'campo']), como a
      // obrigatoriedade de identidade da US-102684 — mapeia para os campos em
      // vez de jogar o array no toast
      if(error.response?.status === 422 && Array.isArray(error.response.data?.detail)) {
        const fieldErrors = {};
        error.response.data.detail.forEach((err) => {
          const fieldName = err.loc?.[1];
          if (fieldName) fieldErrors[fieldName] = err.msg;
        });
        setValidationErrors(fieldErrors);
        setErrorMessage("Por favor, corrija os erros antes de enviar.");
      } else if(error.response?.status === 400 && error.response.data?.detail) {
        showError(error.response.data.detail);
      } else if(error.response?.data?.detail) {
        showError(error.response.data.detail);
      } else {
        showError("Erro de conexão com o servidor.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <FormContainer>
      <FormHeader>Editar Gestante</FormHeader>
      <form onSubmit={handleSubmit}>
        <FormField>
          <CpfInputField
            value={formValues.cpf}
            onChange={handleChange}
            error={validationErrors.cpf}
            readOnly
          />
        </FormField>

        <FormField>
          <label htmlFor="cns">CNS:</label>
          <input
            type="text"
            id="cns"
            name="cns"
            value={formValues.cns || ""}
            onChange={handleChange}
            maxLength={15}
            minLength={15}
            pattern="[0-9]{15}"
            placeholder="Digite os 15 dígitos do CNS"
          />
          {validationErrors.cns && <ValidationError>
            {formatValueErrorMessage(validationErrors.cns)}
            </ValidationError>}
        </FormField>

        <FormField>
          <label htmlFor="nome">Nome da Gestante:</label>
          <input
            type="text"
            id="nome"
            name="nome"
            value={formValues.nome}
            onChange={handleChange}
          />
          {validationErrors.nome && <ValidationError>{validationErrors.nome}</ValidationError>}
        </FormField>

        {podeEditarDataNascimento && (
          <FormField>
            <label htmlFor="data_nascimento">Data de Nascimento:</label>
            <input
              type="date"
              id="data_nascimento"
              name="data_nascimento"
              value={formValues.data_nascimento || ""}
              onChange={handleChange}
              max={hojeISO()}
            />
            {validationErrors.data_nascimento && (
              <ValidationError>
                {formatValueErrorMessage(validationErrors.data_nascimento)}
              </ValidationError>
            )}
          </FormField>
        )}

        <FormField>
          <label htmlFor="municipio_cod_ibge">Município:</label>
          <select
            id="municipio_cod_ibge"
            name="municipio_cod_ibge"
            value={formValues.municipio_cod_ibge}
            onChange={handleChange}
          >
            <option value="">Selecione...</option>
            {Array.isArray(municipios) && municipios.length > 0 ? (
              municipios.map((municipio) => (
                <option key={municipio.cod_ibge} value={municipio.cod_ibge}>
                  {municipio.nome}
                </option>
              ))
            ) : (
              <option disabled>Carregando Municípios...</option>
            )}
          </select>
          {validationErrors.municipio_cod_ibge && <ValidationError>{validationErrors.municipio_cod_ibge}</ValidationError>}
        </FormField>

        {identidadeHabilitada && (
          <IdentidadeGeneroSection
            values={identidade}
            onChange={handleIdentidadeChange}
            errors={validationErrors}
          />
        )}

        <SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Salvando..." : "Salvar Alterações"}
        </SubmitButton>
      </form>

      {successMessage && <Message>{successMessage}</Message>}
      {errorMessage && <Message isError>{errorMessage}</Message>}
    </FormContainer>
      {/* Toast Manager para notificações */}
          <ToastManager toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default EditGestanteForm;
